import apiClient from '../api/client';

/**
 * Service for MEWP specific views.
 */
const mewpService = {
    /**
     * Get MEWP dashboard stats.
     */
    async getDashboardStats() {
        return await apiClient.get('/stats/mewp/dashboard');
    },

    /**
     * Get MEWP monitoring data.
     */
    async getMonitoringData() {
        return await apiClient.get('/stats/mewp/monitoring');
    },

    /**
     * Get detail for a specific MEWP.
     */
    async getDeviceDetail(deviceId) {
        return await apiClient.get(`/devices/${deviceId}/detail`);
    },

    async getDetails(deviceId) {
        return await this.getDeviceDetail(deviceId);
    },

    /**
     * Update device metadata (asset_tag, serial_number, product_category).
     */
    async updateDeviceMetadata(deviceId, data) {
        return await apiClient.put(`/devices/${deviceId}/metadata`, data);
    },

    /**
     * Fleet signal coverage — which signals each installed machine is actually
     * sending (engine / angles / cage load / …), over a rolling window.
     * @param {{hours?: number, frames?: number}} params
     */
    async getSignalCoverage(params = {}) {
        return await apiClient.get('/devices/signal-coverage', { params });
    },

    /**
     * Get history for a specific MEWP.
     */
    async getDeviceHistory(deviceId, limit = 100, date = null) {
        let url = `/devices/${deviceId}/history?limit=${limit}`;
        if (date) {
            url += `&date=${date}`;
        }
        return await apiClient.get(url);
    },


    /**
     * Get available log dates for a specific MEWP.
     */
    async getLogDates(deviceId, startDate = null, endDate = null) {
        let url = `/devices/${deviceId}/log-dates`;
        const params = [];
        if (startDate) params.push(`startDate=${startDate}`);
        if (endDate) params.push(`endDate=${endDate}`);
        if (params.length > 0) url += `?${params.join('&')}`;
        return await apiClient.get(url);
    },


    /**
     * Get asset tracking for a specific MEWP.
     */
    async getDeviceTracking(deviceId) {
        return await apiClient.get(`/tracking/device/${deviceId}`);
    },

    /**
     * Get all asset tracking records.
     */
    async getAllTracking() {
        return await apiClient.get('/tracking');
    },

    /**
     * Get active rental deployments.
     */
    async getRentalDeployments() {
        return await apiClient.get('/tracking/rentals');
    },

    /**
     * Get completed rental deployments.
     */
    async getCompletedRentals() {
        return await apiClient.get('/tracking/completed-rentals');
    },
    async getFleetStats() {
        return await apiClient.get('/tracking/fleet-stats');
    },

    /**
     * End rental for a specific device and trigger service ticket.
     */
    async endRental(deviceId) {
        return await apiClient.patch(`/tracking/device/${deviceId}/end-rental`);
    },

    /**
     * Change machine lifecycle status (super admin / delegated permission).
     * payload = { toStatus, closing: {...}, opening: {...} }
     */
    async changeMachineStatus(deviceId, payload) {
        return await apiClient.post(`/tracking/device/${deviceId}/status`, payload);
    },

    /**
     * Get machine status transition history (asset_status_logs).
     */
    async getStatusHistory(deviceId) {
        return await apiClient.get(`/tracking/device/${deviceId}/status-history`);
    },

    /**
     * Get service history for a specific MEWP.
     */
    async getServiceHistory(deviceId) {
        return await apiClient.get(`/maintenance/history?device_id=${deviceId}`);
    },

    /**
     * Get all invoices.
     */
    async getInvoices() {
        return await apiClient.get('/billing/invoices');
    },

    /**
     * Get single invoice detail with template.
     */
    async getInvoiceDetail(invoiceId) {
        return await apiClient.get(`/billing/invoices/${invoiceId}`);
    },

    /**
     * Get live status for a device (KEY_ON / ACTIVE / KEY_OFF + recent activity).
     */
    async getDeviceLiveStatus(deviceId) {
        return await apiClient.get(`/devices/${deviceId}/live-status`);
    },

    /**
     * Raw device-reported events over an IST day range: device_alerts +
     * device_dtc rows. Feeds the Visualization's "Device Events" panel.
     */
    async getDeviceEvents(deviceId, from, to) {
        const q = new URLSearchParams();
        if (from) q.set('from', from);
        if (to) q.set('to', to);
        const qs = q.toString();
        return await apiClient.get(`/devices/${deviceId}/device-events${qs ? `?${qs}` : ''}`);
    },

    /**
     * Trigger a manual check and update for a device via MQTT.
     */
    async checkAndUpdateDevice(deviceId) {
        return await apiClient.post(`/devices/${deviceId}/check-update`);
    },

    /**
     * Customer Management
     */
    async getCustomers() {
        return await apiClient.get('/customers');
    },

    async getCustomer(id) {
        return await apiClient.get(`/customers/${id}`);
    },

    async updateCustomer(id, data) {
        return await apiClient.put(`/customers/${id}`, data);
    },

    async createCustomer(data) {
        return await apiClient.post('/customers', data);
    },

    /**
     * Contract Management
     */
    async getContracts(status = null) {
        let url = '/contracts';
        if (status) url += `?status=${status}`;
        return await apiClient.get(url);
    },

    async getContractById(id) {
        return await apiClient.get(`/contracts/${id}`);
    },

    /** Raw DB rows for one contract (contract + machine-contract history + customer). */
    async getContractRaw(id) {
        return await apiClient.get(`/contracts/${encodeURIComponent(id)}/raw`);
    },

    /** Every machine-contract line across all contracts (full history). */
    async getAllMachineContracts() {
        return await apiClient.get('/contracts/machine-contracts');
    },

    /**
     * Add a machine to an existing contract. This is the only way a machine
     * joins a contract — contract creation no longer takes equipment, because
     * the machine's own rental start date (effective_from_date, required here)
     * is what its invoice is billed from.
     */
    async createMachineContractRow(payload) {
        return await apiClient.post('/contracts/machine-row', payload);
    },

    /** Correct one machine-contract row in place (shift / dates / status). */
    async updateMachineContractRow(rowId, payload) {
        return await apiClient.put(`/contracts/machine-row/${encodeURIComponent(rowId)}`, payload);
    },

    /** Delete one machine-contract row (clear an unnecessary history line). */
    async deleteMachineContractRow(rowId) {
        return await apiClient.delete(`/contracts/machine-row/${encodeURIComponent(rowId)}`);
    },

    async getContractLiveStatus(contractId) {
        return await apiClient.get(`/devices/contract/${contractId}/live-status`);
    },

    /**
     * Dated usage summary for a contract over a range (day / week / month / custom).
     * from / to are 'YYYY-MM-DD'. Returns per-machine working/engine/extra hours
     * plus contract-level totals — matches the worklog reports exactly.
     */
    async getContractSummary(contractId, from, to) {
        return await apiClient.get(
            `/downloads/contract-summary/${encodeURIComponent(contractId)}?from=${from}&to=${to}`
        );
    },

    async createRentalContract(data) {
        return await apiClient.post('/contracts', data);
    }
};

export default mewpService;
